import {
  Component,
  Input,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import Swal from "sweetalert2";
import { UserManagementService } from "../user-management.service";
import { AlertifyService } from "src/app/UtilityService/alertify.service";

@Component({
    selector: "app-Forgotpass-confirm",
    templateUrl: "./forgotpass-confirm.component.html",
    styleUrls: ["./forgotpass-confirm.component.css"],
    standalone: false
})
export class ForgotpassconfirmComponent implements OnInit, OnDestroy {
  hidePassword = true;
  @Input() username?: any;
  public forgotpassFormConfirm: FormGroup;
  timeLeft: number = 60; // 1 minute timer
  private interval: any;
  isSubmitDisabled: boolean = false;
  captchaText: any = [];
  validCaptcha: boolean = false;
  noiseImage: string = "";
  isSubmitting: boolean = false;
  errorMessage: string = "";

  constructor(
    private fb: FormBuilder,
    private activeModal: NgbActiveModal,
    private alertify: AlertifyService,
    private userService: UserManagementService
  ) {
    this.startTimer();
  }

  ngOnInit(): void {
    this.forgotpassFormConfirm = this.fb.group({
      otp: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]],
      newPassword: ["", [Validators.required, Validators.minLength(8)]],
      captcha: ["", [Validators.minLength(5)]],
    });
    this.generateCaptcha();
  }

  validateCaptcha(captcha: any) {
    const expectedCaptcha = this.captchaText.map((item) => item.char).join("");
    this.validCaptcha = (captcha || "").trim() === expectedCaptcha;
  }

  refreshCaptcha(event?: Event): void {
    this.generateCaptcha(event);
  }

  generateCaptcha(event?: Event): void {
    if (event) {
      const button = (event.target as HTMLElement).closest(".refresh-button");
      if (button) {
        button.classList.add("rotating");
        setTimeout(() => button.classList.remove("rotating"), 1000); // Reset animation
      }
    }

    const possibleCharacters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890!@#$%^&*";
    const captchaLength = 5;

    this.captchaText = this.generateCaptchaText(
      captchaLength,
      possibleCharacters
    );
    this.noiseImage = this.addBackgroundNoise();
    this.forgotpassFormConfirm?.controls?.captcha?.setValue("");
    this.validCaptcha = false;
  }

  generateCaptchaText(
    length: number,
    characters: string
  ): { char: string; style: any }[] {
    return Array.from({ length }, () => {
      const randomChar = this.makeRandom(1, characters);
      const styles = {
        fontSize: `${Math.floor(Math.random() * 23) + 18}px`,
        color: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
        transform: `rotate(${Math.random() * 30 - 15}deg) translate(${
          Math.random() * 10 - 5
        }px, ${Math.random() * 10 - 5}px)`,
        fontWeight: Math.random() > 0.7 ? "bold" : "normal",
        fontStyle: Math.random() > 0.8 ? "italic" : "normal",
        textShadow: `${Math.floor(Math.random() * 5)}px ${Math.floor(
          Math.random() * 6
        )}px ${Math.floor(Math.random() * 6)}px rgba(0, 0, 0, 0.3)`,
        opacity: (Math.random() * 0.3 + 0.7).toFixed(2),
      };

      return { char: randomChar, style: styles };
    });
  }

  makeRandom(length: number, characters: string): string {
    return Array.from({ length }, () =>
      characters.charAt(Math.floor(Math.random() * characters.length))
    ).join("");
  }

  addBackgroundNoise(): string {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = 200;
    canvas.height = 40;

    if (ctx) {
      for (let i = 0; i < 20; i++) {
        ctx.strokeStyle = `rgba(${Math.random() * 255}, ${
          Math.random() * 255
        }, ${Math.random() * 255}, 0.5)`;
        ctx.beginPath();
        ctx.moveTo(Math.random() * 200, Math.random() * 40);
        ctx.lineTo(Math.random() * 200, Math.random() * 40);
        ctx.stroke();
      }

      for (let i = 0; i < 10; i++) {
        ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${
          Math.random() * 255
        }, 0.5)`;
        ctx.beginPath();
        ctx.arc(
          Math.random() * 200,
          Math.random() * 40,
          Math.random() * 5,
          0,
          2 * Math.PI
        );
        ctx.fill();
      }
    }

    return canvas.toDataURL();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  // addFormGroup(): void {
  //   const formGroup = this.fb.group({
  //     inputField: ["", [Validators.required, Validators.maxLength(20)]]
  //   });

  // }

  submit(): void {
    this.errorMessage = "";

    if (this.forgotpassFormConfirm.valid && this.validCaptcha) {
      const confirmationCode = this.forgotpassFormConfirm.controls.otp.value;
      const password = this.forgotpassFormConfirm.controls.newPassword.value;
      const request = {
        username: this.username,
        confirmation_code: confirmationCode,
        new_password: password,
      };
      this.isSubmitting = true;
      this.userService.forgotpassconfirm(request).then((response: any) => {
        this.isSubmitting = false;

        if (response?.success) {
          this.activeModal.dismiss();
          this.showAlert("Password changed successfully.");
        } else {
          this.errorMessage = response?.message || "Invalid confirmation code.";
        }
      }).catch((error: any) => {
        this.isSubmitting = false;
        this.errorMessage =
          error?.error?.message ||
          error?.message ||
          "Password reset failed. Please try again.";
      });
    } else {
      this.forgotpassFormConfirm.markAllAsTouched();
      this.errorMessage = !this.validCaptcha
        ? "Please enter a valid captcha."
        : "Please complete all fields with valid values.";
      this.alertify.error(this.errorMessage);
    }
  }

  public dissmissModal() {
    this.activeModal.dismiss();
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
  startTimer() {
    this.isSubmitDisabled = false;
    this.timeLeft = 600; // Reset the timer (10 mins)
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
      } else {
        this.isSubmitDisabled = true;
        clearInterval(this.interval); // Clear the timer
      }
    }, 1000);
  }

  resendOtp() {
    this.startTimer(); // Restart the timer on resend
  }
  showAlert(msg) {
    Swal.fire({
      title: msg,
      showClass: {
        popup: "animate__animated animate__fadeInDown",
      },
      hideClass: {
        popup: "animate__animated animate__fadeOutUp",
      },
    });
  }
}
